import { format } from 'date-fns';
import { Document } from '@contentful/rich-text-types';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { ArrowUpRightIcon } from '../../../../../components/Icons/ArrowUpRightIcon';
import { RichText } from '../../../../../components/RichText/RichText';
import { Button } from '../../../../../components/Clickables/Buttons/Button';
import { faqRichTextOptions } from '../constants/faqRichTextOptions';
import { ExternalLink } from '../../../../../components/Clickables/ExternalLink/ExternalLink';
import { InternalLink } from '../../../../../components/Clickables/InternalLink/InternalLink';
import { IFAQData } from '../../../../../components/FAQ/FAQ';
import { InSeasonSpecies } from '@domain/regulations/getInSeasonSpecies';
import { formatLinkedSpeciesString } from '../../../helpers/formatLinkedSpeciesString';
import { renderLinkedSpecies } from '../../../helpers/renderLinkedSpecies';
import { FishingReport } from '../../../../../components/FishingReportsGrid/components/FishingReportCard';
import { getIntersectingCountiesLinksString } from '../../../../../components/IntersectingCountiesLinks/getIntersectingCountiesLinksString';
import { IntersectingCountiesLinks } from '../../../../../components/IntersectingCountiesLinks/IntersectingCountiesLinks';
import { LatLng } from '../../../../../components/LatLng/LatLng';
import { getLocationLinksString } from '../../../../../components/LocationLinks/getLocationLinksString';
import { LocationLinks } from '../../../../../components/LocationLinks/LocationLinks';
import { addDefiniteArticleToWaterName } from '@domain/fishingWater/addDefiniteArticleToWaterName';
import { addIndefiniteArticleToWaterType } from '@domain/fishingWater/addIndefiniteArticleToWaterType';
import { getEdgesFromConnection } from '@graphql/connections/getEdgesFromConnection';
import { getSpeciesUrl, getUserUrl } from '@domain/routes/urls';
import { firstNonEmpty } from '@lib/strings/firstNonEmpty';
import {
  FishingWaterCountryFields,
  FishingWaterCountyFields,
  FishingWaterRegionFields,
  FishingWaterTopSpeciesSummaryFields,
  FishingWaterTopSpeciesNodeFields,
  IntersectingCountyNodeFields,
  WaterTypes,
} from '../../../../../generated/graphql';

interface IProps {
  county: FishingWaterCountyFields | null;
  country: FishingWaterCountryFields | null;
  descriptionOfWaterType: WaterTypes;
  fishingWaterName: string;
  handleCTAClick: () => void;
  inSeasonSpecies: InSeasonSpecies[];
  fishingLicenseInfoRichText?: Document;
  licenseUrl?: string;
  intersectingCounties: IntersectingCountyNodeFields[];
  latitude: number;
  longitude: number;
  region: FishingWaterRegionFields | null;
  recentCatchPostList: FishingReport[];
  topSpeciesSummary?: FishingWaterTopSpeciesSummaryFields;
}

const NUMBER_OF_FISHING_REPORTS_TO_SHOW = 5;

export const getFAQData = ({
  county,
  country,
  descriptionOfWaterType,
  fishingWaterName,
  handleCTAClick,
  inSeasonSpecies,
  fishingLicenseInfoRichText,
  licenseUrl,
  intersectingCounties,
  latitude,
  longitude,
  region,
  recentCatchPostList,
  topSpeciesSummary,
}: IProps): IFAQData[] => {
  const countyName = county?.name ?? undefined;
  const countryName = country?.countryName;
  const countryId = country?.id;
  const regionDisplayName = firstNonEmpty(region?.localName, region?.name);
  const speciesEdges = getEdgesFromConnection<
    FishingWaterTopSpeciesNodeFields,
    { node: FishingWaterTopSpeciesNodeFields | null }
  >(topSpeciesSummary);

  const intersectingCountiesString = getIntersectingCountiesLinksString({
    regionId: region?.id,
    regionName: region?.name,
    countyName,
    intersectingCounties,
  });

  return [
    {
      id: '1',
      title: `📍 Where is ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} located?`,
      body: (
        <>
          {`${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} is ${addIndefiniteArticleToWaterType(descriptionOfWaterType)} located in `}
          <LocationLinks
            countyName={countyName}
            regionId={region?.id}
            regionName={region?.name}
            regionDisplayName={regionDisplayName}
            countryId={countryId}
            countryName={countryName}
            className="m-0 text-left"
            LinkClassName="body-text-sm"
            textAs="span"
            textVariant="md"
          />
          {'. '}
          <IntersectingCountiesLinks
            countyName={countyName}
            regionId={region?.id}
            regionName={region?.name}
            intersectingCounties={intersectingCounties}
          />
          {`Its coordinates are: `}{' '}
          <LatLng textVariant="sm" className="inline" latitude={latitude} longitude={longitude} />
          {'.'}
        </>
      ),
      bodyString: `${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} is ${addIndefiniteArticleToWaterType(descriptionOfWaterType)} located in ${getLocationLinksString({ countyName: countyName, regionId: region?.id, regionName: region?.name, regionDisplayName: regionDisplayName, countryId: countryId, countryName: countryName })}.${intersectingCountiesString.length > 0 ? ` It is also intersecting with ${intersectingCountiesString}.` : ''} Its coordinates are: ${latitude}, ${longitude}.`,
    },
    {
      id: '2',
      title: `🎣 Where on ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} is it best to fish?`,
      body: (
        <>
          {`Find the best fishing spots on ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} by looking at where other anglers have caught fish. This can easily be done in the `}
          <Button
            onClick={handleCTAClick}
            size="sm"
            className="body-text-sm text-primary font-semibold"
            variant="textNone"
          >
            Fishbrain app
          </Button>
          {` where it is also possible to see big fish potential and how good fishing is right now along with Garmin® depth maps.`}
        </>
      ),
      bodyString: `Find the best fishing spots on ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} by looking at where other anglers have caught fish. This can easily be done in the Fishbrain app where it is also possible to see big fish potential and how good fishing is right now along with Garmin® depth maps.`,
    },
    ...(speciesEdges.length > 0
      ? [
          {
            id: '3',
            title: `🐟 What species are in ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)}?`,
            body: (
              <>
                {`The most common species in ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} are: `}
                <ul className="m-0 list-disc px-4 py-2">
                  {speciesEdges.map(({ node }) => {
                    return (
                      <li className="body-text-sm m-0" key={`species-${node._id}`}>
                        <InternalLink
                          to={getSpeciesUrl(node._id, node.displayName)}
                          className="body-text-sm text-primary"
                        >
                          {node.displayName}
                        </InternalLink>
                        {` - ${node.anglers.topAnglers?.totalCount} members reported to have caught this fish`}
                      </li>
                    );
                  })}
                </ul>
              </>
            ),
            // nosemgrep: javascript.lang.security.html-in-template-string
            bodyString: `The most common species in ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} are: <ul>${speciesEdges
              .map(
                ({ node }) =>
                  // nosemgrep: javascript.lang.security.html-in-template-string
                  `<li><a href="${getSpeciesUrl(node._id, node.displayName)}">${node.displayName}</a> - ${node.anglers.topAnglers?.totalCount} members reported to have caught this fish</li>`,
              )
              .join('')}</ul>.`,
          },
        ]
      : []),
    ...(recentCatchPostList.length > 0
      ? [
          {
            id: '4',
            title: `📢 What are the latest ${fishingWaterName} fishing reports?`,
            body: (
              <>
                {`The latest ${fishingWaterName} fishing reports are: `}
                <ul className="m-0 list-disc px-4 py-2">
                  {recentCatchPostList.slice(0, NUMBER_OF_FISHING_REPORTS_TO_SHOW).flatMap(post => {
                    const userNickname = post.user.nickname;
                    const speciesId = post.catch?.species?._id;
                    const speciesName = post.catch?.species?.displayName;
                    if (!userNickname || !speciesId || !speciesName) {
                      return [];
                    }
                    return (
                      <li className="body-text-sm m-0" key={`post-${post._id}`}>
                        <InternalLink
                          to={getUserUrl(userNickname)}
                          className="body-text-sm text-primary"
                        >
                          {`@${userNickname}`}
                        </InternalLink>
                        {` reported a nice `}
                        <InternalLink
                          to={getSpeciesUrl(speciesId, speciesName)}
                          className="body-text-sm text-primary"
                        >
                          {speciesName}
                        </InternalLink>
                        {`.`}
                      </li>
                    );
                  })}
                </ul>
              </>
            ),
            // nosemgrep: javascript.lang.security.html-in-template-string
            bodyString: `The latest ${fishingWaterName} fishing reports are: <ul>${recentCatchPostList
              .slice(0, NUMBER_OF_FISHING_REPORTS_TO_SHOW)
              .flatMap(post => {
                const userNickname = post.user.nickname;
                const speciesId = post.catch?.species?._id;
                const speciesName = post.catch?.species?.displayName;
                if (!userNickname || !speciesId || !speciesName) {
                  return [];
                }
                // nosemgrep: javascript.lang.security.html-in-template-string
                return `<li><a href="${getUserUrl(userNickname)}">@${userNickname}</a> reported a nice <a href="${getSpeciesUrl(
                  speciesId,
                  speciesName,
                )}">${speciesName}</a>.</li>`;
              })
              .join('')}</ul>.`,
          },
        ]
      : []),
    ...(inSeasonSpecies.length > 0
      ? (() => {
          const currentMonth = format(new Date(), 'MMMM');
          const waterName = addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType);
          return [
            {
              id: 'in-season',
              title: `🗓️ What species are in season at ${waterName} right now?`,
              body: (
                <>
                  {`According to fishing regulations at ${waterName}, some of the species in season this ${currentMonth} include: `}
                  {renderLinkedSpecies(inSeasonSpecies)}
                  {`. For full details on other species — including bag limits, minimum and maximum size limits, and seasonal closures — download the `}
                  <Button
                    onClick={handleCTAClick}
                    size="sm"
                    className="body-text-sm text-primary font-semibold"
                    variant="textNone"
                  >
                    Fishbrain app
                  </Button>
                  {'.'}
                </>
              ),
              bodyString: `According to fishing regulations at ${waterName}, some of the species in season this ${currentMonth} include: ${formatLinkedSpeciesString(inSeasonSpecies)}. For full details on other species — including bag limits, minimum and maximum size limits, and seasonal closures — download the Fishbrain app.`,
            },
          ];
        })()
      : []),
    ...(fishingLicenseInfoRichText && licenseUrl
      ? (() => {
          const waterName = addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType);
          const url = licenseUrl;
          return [
            {
              id: 'license',
              title: `🪪 Do I need a fishing license to fish at ${waterName}?`,
              body: (
                <>
                  <RichText document={fishingLicenseInfoRichText} options={faqRichTextOptions} />
                  <ExternalLink
                    href={url}
                    className="body-text-sm text-primary mt-2 inline-flex items-center gap-1"
                    target="_blank"
                  >
                    Get license <ArrowUpRightIcon size={16} />
                  </ExternalLink>
                </>
              ),
              // nosemgrep: javascript.lang.security.html-in-template-string
              bodyString: `${documentToHtmlString(fishingLicenseInfoRichText)} <a href="${url}">Get license</a>`,
            },
          ];
        })()
      : []),
  ];
};
